import { Box, GridItem } from '@chakra-ui/react';
import { upsertFolder } from 'api/mediaFolders';
import { ReactComponent as ImageIcon } from 'assets/icons/folderImage.svg';
import { IMAGE_FOLDER_CREATED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';

import { FolderItem } from './FolderItem';
import { FolderProps } from './Folders.helper';
import NewFolderButton from './NewFolderButton';

interface ManageFolderMenuProps {
  usersFolders: FolderProps[];
  selectFolder: (folder: FolderProps) => void;
  selectImages: (imageId: string[]) => void;
  selectedFolder: FolderProps | null;
  createOnOpen?: boolean;
  selectedImages: string[];
}
export const ManageFolderMenu = ({
  usersFolders,
  selectFolder,
  selectedFolder,
  createOnOpen = true,
  selectedImages,
  selectImages,
}: ManageFolderMenuProps) => {
  const [editOnCreate, setEditOnCreate] = useState('');
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu.folders' });
  const { mutate } = useSWRConfig();
  const toast = useToast();

  /**
   * Handler to add a new folder
   */
  const addNewFolder = useCallback(async () => {
    const results = await upsertFolder({
      name: t('new'),
    });

    if (results.status === 'fail') {
      toast({
        title: t('genericError'),
        description: results.message,
        status: 'error',
      });
      return;
    }

    Mixpanel.track(IMAGE_FOLDER_CREATED.eventName);

    await mutate(GET_USER_MEDIA_API);
    selectFolder({
      label: results.folder.name,
      value: String(results.folder.id),
    });
    setEditOnCreate(String(results.folder.id));
  }, [mutate, selectFolder, t, toast]);

  /**
   * Handler to update folder
   */
  const onEdit = useCallback(
    async (newName: string) => {
      // clean up on edit id
      setEditOnCreate('');
      if (selectedFolder?.value && newName !== selectedFolder.label) {
        const results = await upsertFolder({
          id: selectedFolder.value,
          name: newName,
        });

        if (results.status === 'fail') {
          toast({
            title: t('genericError'),
            description: results.message,
            status: 'error',
          });
          return;
        }
        selectFolder({
          label: results.folder.name,
          value: String(results.folder.id),
          medias: selectedFolder.medias,
        });
        mutate(GET_USER_MEDIA_API);
      }
    },
    [mutate, selectFolder, selectedFolder, t, toast],
  );

  const onSelectFolder = (folder: FolderProps) => {
    selectFolder(folder);
  };

  // should create a folder when open the modal
  useEffect(() => {
    if (createOnOpen) {
      addNewFolder();
    }
  }, [addNewFolder, createOnOpen]);

  return (
    <GridItem
      area={'nav'}
      data-testid='manage-folder-menu'
      style={{
        borderRight: '1px solid',
        borderRightColor: 'var(--vg-colors-divider-gray)',
        backgroundColor: 'var(--vg-colors-black-alpha-50)',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
      }}
    >
      <Box p={4} w={'100%'} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
        <Box>
          <FolderItem
            isActive={selectedFolder?.value === ''}
            onClick={onSelectFolder}
            selectImages={selectImages}
            disableEditing={true}
            selectedImages={selectedImages}
            selectedFolder={selectedFolder || undefined}
            folder={{
              value: '',
              label: t('allUploads'),
              icon: <ImageIcon width='20px' height='20px' />,
            }}
            buttonProps={{
              _active: {
                bg: 'hover.blue',
                '& path': {
                  fill: 'upgrade.blue.700',
                },
                color: 'upgrade.blue.700',
              },
            }}
          />
          <Box pl={4} pt={2} pr={2} minH='calc(63vh)' maxH='calc(63vh)' overflow='auto'>
            {usersFolders?.map((folder) => (
              <FolderItem
                folder={folder}
                isActive={String(selectedFolder?.value) === String(folder.value)}
                key={folder.value}
                onClick={onSelectFolder}
                selectImages={selectImages}
                isEditing={editOnCreate === folder.value}
                selectedImages={selectedImages}
                selectedFolder={selectedFolder || undefined}
                onEdit={onEdit}
              />
            ))}
          </Box>
        </Box>

        <NewFolderButton
          iconColor='var(--vg-colors-chakra-body-text)'
          buttonProps={{ variant: 'outline', size: 'sm', w: '100%' }}
          onClick={addNewFolder}
          text={t('new')}
        />
      </Box>
    </GridItem>
  );
};
